/**
 * Feature Flags
 * Control feature rollout and A/B testing
 */

export interface FeatureFlag {
  enabled: boolean;
  description: string;
  rolloutPercentage: number; // 0-100
  isDefault?: boolean;
}

export const FEATURE_FLAGS: Record<string, FeatureFlag> = {
  AUTO_SAVE_DATA_ENTRY: {
    enabled: true,
    description: 'Real-time field-by-field auto-save for production entries',
    rolloutPercentage: 100, // Start at 100% for full deployment
    isDefault: true,
  },
  VOICE_ENTRY: {
    enabled: true,
    description: 'Voice-driven data entry',
    rolloutPercentage: 100,
  },
  OCR_GAUGE_READING: {
    enabled: true,
    description: 'OCR for automatic gauge reading from photos',
    rolloutPercentage: 100,
  },
  SATELLITE_VIEW: {
    enabled: true,
    description: 'Satellite imagery for well locations',
    rolloutPercentage: 100,
  },
  ROUTE_OPTIMIZATION: {
    enabled: true,
    description: 'TSP-based route optimization',
    rolloutPercentage: 100,
  },
};

/**
 * Check if a feature is enabled
 */
export const isFeatureEnabled = (featureName: keyof typeof FEATURE_FLAGS): boolean => {
  const flag = FEATURE_FLAGS[featureName];
  if (!flag) return false;
  
  // If not enabled at all, return false
  if (!flag.enabled) return false;
  
  // If 100% rollout, return true
  if (flag.rolloutPercentage === 100) return true;
  
  // For partial rollout, use random (in production, use user ID hash)
  // This is simplified - in production, hash user ID for consistent experience
  return Math.random() * 100 < flag.rolloutPercentage;
};

/**
 * Get feature flag details
 */
export const getFeatureFlag = (featureName: keyof typeof FEATURE_FLAGS): FeatureFlag | null => {
  return FEATURE_FLAGS[featureName] || null;
};
